export const ALIAS_MIN_PLAYERS = 4;
export const ALIAS_MAX_PLAYERS = 40;
export const ALIAS_MIN_TEAMS = 2;
export const ALIAS_MAX_TEAMS = 8;

export type AliasRoomStatus = 'lobby' | 'running' | 'finished' | 'cancelled';
export type AliasConnectionStatus = 'not_joined' | 'connected' | 'disconnected' | 'spectator';
export type AliasGameMode = 'fixed_rounds' | 'target_score';
export type AliasPhase =
  | 'LOBBY'
  | 'BETWEEN_TURNS'
  | 'TURN_PREPARATION'
  | 'TURN_COUNTDOWN'
  | 'TURN_ACTIVE'
  | 'TURN_REVIEW'
  | 'ROUND_RESULTS'
  | 'PAUSED'
  | 'MATCH_FINISHED';
export type AliasDifficulty = 'easy' | 'medium' | 'hard';
export type AliasAgeRating = '6+' | '10+' | '12+' | '16+' | '18+';
export type AliasWordPackId = 'base' | 'family' | 'travel' | 'culture' | 'work';
export type AliasActionType = 'GUESSED' | 'SKIPPED' | 'MANUAL_ADJUSTMENT';
export type AliasErrorCode =
  | 'ACTION_NOT_ALLOWED'
  | 'INVALID_MESSAGE'
  | 'INVALID_NAME'
  | 'INVALID_TARGET'
  | 'PLAYER_NOT_FOUND'
  | 'ROOM_NOT_FOUND'
  | 'ROOM_STARTED'
  | 'ROOM_FINISHED'
  | 'TURN_ALREADY_FINISHED'
  | 'WORD_ALREADY_RESOLVED'
  | 'ALREADY_USED';

export interface AliasGameSettings {
  mode: AliasGameMode;
  roundCount: number;
  targetScore: number;
  turnDurationSeconds: number;
  skipPenalty: number;
  allowNegativeTurnScore: boolean;
  reviewRequired: boolean;
  difficulty: AliasDifficulty;
  ageRating: AliasAgeRating;
  wordPackIds: AliasWordPackId[];
  teamOrderMode: 'manual' | 'random';
}

export const DEFAULT_ALIAS_SETTINGS: AliasGameSettings = {
  mode: 'fixed_rounds',
  roundCount: 3,
  targetScore: 30,
  turnDurationSeconds: 60,
  skipPenalty: -1,
  allowNegativeTurnScore: true,
  reviewRequired: true,
  difficulty: 'medium',
  ageRating: '12+',
  wordPackIds: ['base', 'family'],
  teamOrderMode: 'manual',
};

export interface AliasTeamSeed {
  name: string;
  colorKey?: string;
  icon?: string;
  playerNames: string[];
}

export interface AliasPlayerPublic {
  playerId: string;
  name: string;
  isCreator: boolean;
  status: AliasConnectionStatus;
  isReady: boolean;
  teamId: string | null;
}

export interface AliasTeamPublic {
  teamId: string;
  name: string;
  colorKey: string;
  icon: string;
  order: number;
  score: number;
  playerIds: string[];
  playerNames: string[];
  nextExplainerPlayerId: string | null;
}

export interface AliasWordView {
  id: string;
  text: string;
  category: string;
}

export interface AliasTurnActionView {
  id: string;
  type: AliasActionType;
  word: AliasWordView | null;
  scoreDelta: number;
  initiatedByPlayerId: string | null;
  createdAt: string;
  reason: string | null;
}

export interface AliasTurnPublic {
  turnId: string;
  roundNumber: number;
  turnIndex: number;
  teamId: string;
  explainerPlayerId: string;
  explainerName: string;
  status: AliasPhase;
  startedAt: string | null;
  endsAt: string | null;
  provisionalScore: number;
  confirmedScore: number | null;
  guessedCount: number;
  skippedCount: number;
  actions: AliasTurnActionView[];
}

export interface AliasScoreLedgerEntry {
  id: string;
  teamId: string;
  turnId: string | null;
  delta: number;
  reason: string;
  createdByPlayerId: string | null;
  createdAt: string;
}

export interface AliasRoomSummary {
  roomId: string;
  code: string;
  joinUrl: string;
  qrPayload: string;
  status: AliasRoomStatus;
  phase: AliasPhase;
  settings: AliasGameSettings;
  currentRound: number;
  currentTurnIndex: number;
  stateVersion: number;
  players: AliasPlayerPublic[];
  teams: AliasTeamPublic[];
  activeTeamId: string | null;
  activeExplainerPlayerId: string | null;
  endsAt: string | null;
}

export interface AliasPrivateState {
  screen: 'join' | 'lobby' | 'preparing' | 'countdown' | 'active' | 'review' | 'round_results' | 'finished' | 'waiting' | 'paused';
  phase: AliasPhase;
  stateVersion: number;
  viewerPlayerId: string;
  viewerName: string;
  isCreator: boolean;
  isReady: boolean;
  teamId: string | null;
  currentRound: number;
  currentTurnIndex: number;
  settings: AliasGameSettings;
  players: AliasPlayerPublic[];
  teams: AliasTeamPublic[];
  activeTeamId: string | null;
  activeExplainerPlayerId: string | null;
  activeExplainerName: string | null;
  endsAt: string | null;
  currentWord: AliasWordView | null;
  canStartTurn: boolean;
  canResolveWord: boolean;
  canConfirmTurn: boolean;
  canContinueRound: boolean;
  canPause: boolean;
  turn: AliasTurnPublic | null;
  ledger: AliasScoreLedgerEntry[];
  winnerTeamIds: string[];
  paused: boolean;
}

export interface AliasPublicState {
  phase: AliasPhase;
  status: AliasRoomStatus;
  stateVersion: number;
  currentRound: number;
  currentTurnIndex: number;
  players: AliasPlayerPublic[];
  teams: AliasTeamPublic[];
  activeTeamId: string | null;
  activeExplainerPlayerId: string | null;
  activeExplainerName: string | null;
  endsAt: string | null;
  turn: AliasTurnPublic | null;
  winnerTeamIds: string[];
}

export interface AliasWordEntry {
  id: string;
  text: string;
  category: string;
  packId: AliasWordPackId;
  difficulty: AliasDifficulty;
  ageRating: AliasAgeRating;
}
